











































































import {Vue, Component, Prop, Watch} from 'vue-property-decorator'
import FilterMyWalletOrbit from '@/components/filters/FilterMyWalletOrbit.vue'
import MyWalletOrbitScrollableTable from '@/components/myWallet/MyWalletOrbitScrollableTable.vue'
import OrbitClaimModal, {
  OrbitIndividualReward,
} from '@/components/orbit/OrbitClaimModal.vue'
import OrbitClaimBar, {
  OrbitClaimBarItem,
} from '@/components/orbit/OrbitClaimBar.vue'
import OrbitCountdown from '@/components/orbit/OrbitCountdown.vue'
import {OrbitRoundRewardCollection} from '@/model/collection/OrbitRoundRewardCollection'
import {OrbitTransactionCollection} from '@/model/collection/OrbitTransactionCollection'
import {OrbitRoundReward} from '@/model/resource/OrbitRoundReward'
import {$} from '@/facade'
import {OrbitProjectCollection} from '@/model/collection/OrbitProjectCollection'
import {OrbitHelper} from '@/helpers/OrbitHelper'

@Component({
  components: {
    OrbitClaimBar,
    OrbitClaimModal,
    OrbitCountdown,
    MyWalletOrbitScrollableTable,
    FilterMyWalletOrbit,
  },
})
export default class MyWalletOrbitTableContent extends Vue {
  @Prop({type: Number, required: true}) lastRound!: number
  @Prop({type: Number, required: true}) activeRound!: number

  orbitRewardCollection = new OrbitRoundRewardCollection()
  orbitProjectCollection = new OrbitProjectCollection()

  computedRound = 0

  transactionCountMap: Record<
    number,
    {
      total: string
      your: string
    }
  > = {}

  orbitClaimBarItems: OrbitClaimBarItem[] = []
  orbitIndividualRewards: OrbitIndividualReward[] = []
  unclaimedRewards: OrbitRoundReward[] = []

  showClaimModal = false
  roundEndTimestampMs: number | null = null
  claimEndByRound: Record<number, number> = {}
  nowMs = Date.now()
  claimDeadlineInterval: number | null = null

  get hasNoUnclaimedRewards(): boolean {
    return this.unclaimedRewards.length === 0
  }

  get hasApprovedRewards(): boolean {
    return (
      this.orbitRewardCollection.items.filter(
        item => item.status === 'approved' || item.status === 'claimed'
      ).length > 0
    )
  }

  get hasClaimedRewards(): boolean {
    return (
      this.orbitRewardCollection.items.filter(item => item.status === 'claimed')
        .length > 0
    )
  }

  get allRewardsClaimed(): boolean {
    // Only show "all rewards claimed" if there were approved rewards AND all of them were claimed
    return (
      this.hasApprovedRewards &&
      this.hasNoUnclaimedRewards &&
      this.hasClaimedRewards
    )
  }

  get hasClaimPeriodEnded(): boolean {
    const hasExpiredRewards = this.orbitRewardCollection.items.some(
      reward => reward.status === 'expired'
    )
    return hasExpiredRewards && this.unclaimedRewards.length === 0
  }

  get isClaimDisabled(): boolean {
    return this.hasNoUnclaimedRewards || this.hasClaimPeriodEnded
  }

  get earliestClaimDeadlineMs(): number | null {
    const unclaimed = this.unclaimedRewards
    if (unclaimed.length === 0) return null

    const now = this.nowMs
    let earliest: number | null = null

    for (const r of unclaimed) {
      const ms = this.getExpirationMs(r)
      if (ms != null && ms > now && (earliest == null || ms < earliest)) {
        earliest = ms
      }
    }

    return earliest
  }

  mounted() {
    this.computedRound = this.activeRound || this.lastRound
    this.populateResources()
    this.fetchRoundEndTime()
    this.startClaimDeadlineTicker()
  }

  beforeDestroy() {
    this.stopClaimDeadlineTicker()
  }

  startClaimDeadlineTicker() {
    this.stopClaimDeadlineTicker()
    this.claimDeadlineInterval = window.setInterval(() => {
      this.nowMs = Date.now()
    }, 1000)
  }

  stopClaimDeadlineTicker() {
    if (this.claimDeadlineInterval != null) {
      clearInterval(this.claimDeadlineInterval)
      this.claimDeadlineInterval = null
    }
  }

  getExpirationMs(reward: OrbitRoundReward): number | null {
    return OrbitHelper.claimEndMsForReward(reward, this.claimEndByRound)
  }

  isRewardExpired(reward: OrbitRoundReward): boolean {
    if (reward.status === 'expired') return true
    const ms = this.getExpirationMs(reward)
    return ms != null && ms <= this.nowMs
  }

  isRewardClaimable(reward: OrbitRoundReward): boolean {
    if (reward.status !== 'approved') return false
    return !this.isRewardExpired(reward)
  }

  async fetchRoundEndTime() {
    if (this.activeRound <= 0) {
      this.roundEndTimestampMs = null
      return
    }
    try {
      const roundEndTime = await OrbitHelper.getRoundEndTimeMs()
      const endTimestamp = Number(roundEndTime)
      if (isNaN(endTimestamp) || endTimestamp <= 0) {
        this.roundEndTimestampMs = null
        return
      }
      const endMs = endTimestamp < 1e12 ? endTimestamp * 1000 : endTimestamp
      if (endMs <= Date.now()) {
        this.roundEndTimestampMs = null
        return
      }
      this.roundEndTimestampMs = endMs
    } catch {
      this.roundEndTimestampMs = null
    }
  }

  async populateResources() {
    this.$await.init('populateMyWalletRound')

    if (!$.walletAdapter.n3Address) {
      this.orbitRewardCollection.items = []
      this.orbitProjectCollection.items = []
      this.transactionCountMap = {}
      this.orbitClaimBarItems = []
      this.showClaimModal = false
      this.claimEndByRound = {}
      await this.fetchRoundEndTime()
      this.$await.done('populateMyWalletRound')
      return
    }

    this.orbitRewardCollection.address = $.walletAdapter.n3Address
    this.orbitRewardCollection.round = this.computedRound

    this.orbitProjectCollection.round = [this.computedRound]

    await this.orbitRewardCollection.queryAsPage()
    await this.orbitProjectCollection.queryAsPage()

    this.claimEndByRound = await OrbitHelper.buildClaimEndByRoundMap(
      this.orbitRewardCollection.items || []
    )

    await this.populateTransactionCountMap(
      $.walletAdapter.n3Address ?? undefined
    )
    await this.populateClaimables()

    this.populateOrbitClaimBar()
    await this.populateOrbitClaimAmount()
    await this.fetchRoundEndTime()

    this.$await.done('populateMyWalletRound')
  }

  handleOpenClaimModal() {
    if (
      !$.walletAdapter.n3Address ||
      this.orbitClaimBarItems.length === 0 ||
      this.hasClaimPeriodEnded
    )
      return

    this.showClaimModal = true
  }

  async handleCloseClaimModal() {
    this.showClaimModal = false
    await this.populateResources()
    this.$emit('claim-finished')
  }

  async populateOrbitClaimAmount() {
    const rewards: OrbitIndividualReward[] = []

    const instanceIdCache = new Map<string, number>()

    for (const item of this.unclaimedRewards) {
      if (!item.orbitProject) continue

      const projectScriptHash = item.orbitProject.contract?.hash || ''
      if (!projectScriptHash) continue

      let instanceId: number

      if (instanceIdCache.has(projectScriptHash)) {
        instanceId = instanceIdCache.get(projectScriptHash)!
      } else {
        const onChainInstanceId = await OrbitHelper.getProjectInstanceId(
          projectScriptHash
        )
        instanceId = Number(onChainInstanceId)
        instanceIdCache.set(projectScriptHash, instanceId)
      }

      rewards.push({
        instanceId: Number(instanceId),
        projectScriptHash,
        amount: Number(item.amount) || 0,
      })
    }

    this.orbitIndividualRewards = rewards
  }

  populateOrbitClaimBar() {
    const totalAmount = new Map<string, number>()

    const unclaimedRewards = this.unclaimedRewards

    unclaimedRewards.forEach(item => {
      if (!item.tokenPriced) return

      const tokenKey =
        item.tokenPriced.hash || item.tokenPriced.symbol || 'unknown'

      const amount =
        Number(item.amount || 0) / 10 ** (item.tokenPriced.decimals ?? 0)

      if (totalAmount.has(tokenKey)) {
        totalAmount.set(tokenKey, totalAmount.get(tokenKey)! + amount)
      } else {
        totalAmount.set(tokenKey, amount)
      }
    })

    const totalAmountArray: OrbitClaimBarItem[] = Array.from(
      totalAmount.entries()
    ).map(([tokenKey, amount]) => ({
      tokenImage:
        unclaimedRewards.find(item => item.tokenPriced?.hash === tokenKey)
          ?.tokenPriced?.imageUrl || '',
      tokenSymbol:
        unclaimedRewards.find(item => item.tokenPriced?.hash === tokenKey)
          ?.tokenPriced?.symbol || '',
      totalAmount: amount,
    }))

    this.orbitClaimBarItems = totalAmountArray
  }

  async populateTransactionCountMap(walletAddress?: string) {
    this.transactionCountMap = {}

    const promises = this.orbitProjectCollection.items.map(async item => {
      const projectId = item.idOrbitProjectPk

      const orbitTransactionCollection = new OrbitTransactionCollection()

      orbitTransactionCollection.round = this.computedRound
      orbitTransactionCollection.idOrbitProjectFk = projectId
      orbitTransactionCollection.limit = 1

      await orbitTransactionCollection.queryAsPage()

      const totalTransactions = String(orbitTransactionCollection.total)

      if (walletAddress) {
        orbitTransactionCollection.address = walletAddress

        await orbitTransactionCollection.queryAsPage()

        const totalYourTransactions = String(orbitTransactionCollection.total)

        this.$set(this.transactionCountMap, projectId, {
          total: totalTransactions,
          your: totalYourTransactions,
        })

        return
      }

      this.$set(this.transactionCountMap, projectId, {
        total: totalTransactions,
        your: 'N/A',
      })

      return
    })

    await Promise.all(promises)
  }

  async populateClaimables() {
    const addr = $.walletAdapter.n3Address
    if (!addr) {
      this.orbitClaimBarItems = []
      this.unclaimedRewards = []
      return
    }

    this.unclaimedRewards = this.orbitRewardCollection.items.filter(reward =>
      this.isRewardClaimable(reward)
    )

    const unclaimedRewards = this.unclaimedRewards

    const tokenMap = new Map<
      string,
      {
        tokenImage: string
        tokenSymbol: string
        amount: number
        decimals: number
      }
    >()

    unclaimedRewards.forEach(reward => {
      if (!reward.tokenPriced) return

      const tokenKey =
        reward.tokenPriced.hash || reward.tokenPriced.symbol || 'unknown'
      const decimals = Number(reward.tokenPriced.decimals) || 8
      const amount = Number(reward.amount) || 0
      const amountInUnits = amount / Math.pow(10, decimals)

      if (tokenMap.has(tokenKey)) {
        const existing = tokenMap.get(tokenKey)!
        existing.amount += amountInUnits
      } else {
        tokenMap.set(tokenKey, {
          tokenImage:
            reward.tokenPriced.imageUrl ||
            require('@/assets/img/orbit-logo.png'),
          tokenSymbol: reward.tokenPriced.symbol || 'TOKEN',
          amount: amountInUnits,
          decimals,
        })
      }
    })
  }

  async handleRoundSelected(roundSelected: string | number) {
    const round = Number(roundSelected)
    if (isNaN(round) || round <= 0) return

    this.computedRound = round
    await this.populateResources()
  }

  @Watch('$walletAdapter.n3Address')
  async onWalletChange() {
    await this.populateResources()
  }
}
